import { useEffect, type RefObject } from 'react';
import { smoothScrollIntoView } from '@/hooks/smooth-scroll-into-view';

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const STABILIZE_TIMEOUT = 500;
const FALLBACK_DELAY = 350;

/**
 * Scrolls focused form inputs into view on mobile after the virtual keyboard opens.
 * Uses visualViewport.resize stabilization to detect when the keyboard has finished
 * animating, with a fallback timeout for browsers without visualViewport support.
 */
export function useScrollToFocusedInput(
  containerRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (!INPUT_TAGS.has(target.tagName)) return;

      const vv = window.visualViewport;
      if (!vv) {
        // Fallback: no visualViewport API
        setTimeout(() => {
          smoothScrollIntoView(target, { behavior: 'smooth', block: 'center' });
        }, FALLBACK_DELAY);
        return;
      }

      // Wait for keyboard animation to finish by detecting viewport height stabilization
      let lastHeight = vv.height;
      let stableCount = 0;
      let rafId: number;
      const startTime = Date.now();

      const checkStable = () => {
        const currentHeight = vv.height;

        if (currentHeight === lastHeight) {
          stableCount++;
        } else {
          stableCount = 0;
          lastHeight = currentHeight;
        }

        // Stable for 2 consecutive checks, or timeout reached
        if (stableCount >= 2 || Date.now() - startTime > STABILIZE_TIMEOUT) {
          smoothScrollIntoView(target, { behavior: 'smooth', block: 'center' });
          return;
        }

        rafId = requestAnimationFrame(checkStable);
      };

      // Start checking after a brief initial delay to let the keyboard start opening
      setTimeout(() => {
        rafId = requestAnimationFrame(checkStable);
      }, 100);

      // Cleanup if focus leaves before stabilization
      const cleanup = () => {
        cancelAnimationFrame(rafId);
        target.removeEventListener('blur', cleanup);
      };
      target.addEventListener('blur', cleanup, { once: true });
    };

    container.addEventListener('focusin', handleFocusIn);
    return () => container.removeEventListener('focusin', handleFocusIn);
  }, [containerRef, enabled]);
}
